import {
    mergeTranslations,
    TranslationMessages as BaseTranslationMessages,
} from 'react-admin';
import frenchMessages from 'ra-language-french';
import { RaTreeTranslationMessages } from '@react-admin/ra-tree';

export interface TranslationMessages
    extends RaTreeTranslationMessages, BaseTranslationMessages {}

const customFrenchMessages: TranslationMessages = mergeTranslations(
    frenchMessages,
    {
        'ra-tree': {
            action: {
                add_root: 'Ajouter une catégorie de produits',
            },
        },
        'ra-search': {
            result: `1 résultat |||| %{smart_count} résultats`,
        },
        merge: {
            workspace: {
                help: 'Aide',
                help_auto_open: 'Ouvrir l’aide automatiquement',
                help_overview: 'Flux et indicateurs de licence.',
                help_export: 'Exportez une proposition pour vérification par l’établissement.',
                help_merge: 'Vérifiez les comptes approuvés, le compte conservé et l’identifiant.',
                help_report: 'Exportez le rapport des fusions pour l’établissement.',
                help_history: 'Consultez les fusions effectuées et annulez-les si nécessaire.',
                license_legend_title: 'Indicateurs de licence',
                license_legend_valid: 'Licence actuellement valide',
                license_legend_not_valid: 'Licence active (non valide actuellement)',
                license_legend_extended: 'Licence prolongée (versions antérieures)',
                license_legend_overlapping_sample: 'Licence de l’établissement',
                license_legend_overlapping: 'Indicateur plein = licence actuellement valide en double de l’établissement',
                help_export_privacy_title: 'Finalité et confidentialité des données',
                help_export_groups_title: 'Sélection et exclusion des groupes pour l’export',
                help_export_indicators_title: 'Détails des lignes et indicateurs visuels',
                help_export_sort_title: 'Tri, recherche et filtres',
                help_merge_reminder_title: 'Vérification et responsabilités',
                help_merge_tools_title: 'Outils de sélection',
                help_merge_roles_title: 'Choix du compte principal et de l’identifiant',
                help_merge_details_title: 'Détails des lignes et filtres',
                help_legend_primary: 'Compte principal (compte conservé)',
                help_legend_source: 'Compte à fusionner (rattaché au principal)',
                help_legend_created_warning:
                    'Compte créé : fin présumée des études (avertissement pour étudiants > 3 ans, élèves > 9 ans ; l’année commence le 1er août)',
                help_legend_switch_included: 'Inclus dans Excel (interrupteur activé)',
                help_legend_switch_excluded:
                    'Exclu d’Excel (bordure en pointillés autour du groupe)',
                help_legend_mpass_locked:
                    'Protection homonymes MPASS : les groupes avec au moins deux comptes MPASS sont verrouillés hors de l’export',
                title: 'Gestion des comptes en double',
                role: 'Rôle utilisateur',
                export: 'Exporter les doublons vers Excel',
                merge: 'Fusion',
                report: 'Rapport',
                history: 'Historique des fusions',
                coming_soon:
                    'La fonctionnalité de fusion est actuellement en phase de test et sera bientôt accessible plus largement.',
            },
            history: {
                mpass_status: 'Profil MPASS',
                report_not_recorded: 'Non enregistré',
                report_estimate_help: 'Les valeurs marquées d’un astérisque utilisent le calcul des produits en double et les listes de licences enregistrés lors de la fusion, et non une variation mesurée de consommation. Les résultats mesurés restent prioritaires.',
                report_missing_audit: 'Certaines anciennes fusions ne contiennent pas le nombre de licences libérées ou leur détail par produit. « Non enregistré » ne signifie pas zéro. Les licences actuelles et les estimations de l’aperçu ne permettent pas de reconstituer ces résultats de manière fiable.',
                mpass_moved: 'Transféré',
                mpass_merged: 'Fusionné',
                mpass_retained: 'Conservé',
                mpass_none: 'Aucun profil',
                mpass_not_transferred: 'Non transféré',
                mpass_unknown: 'Inconnu',
                mpass_help: 'État MPASS au moment de la fusion.',
                mpass_cancelled: 'Avant annulation',
                report_title: 'Rapport des fusions pour l’établissement',
                report_help: 'Le dernier lot comprend toutes les fusions du dernier jour de fusion pour l’établissement et le rôle sélectionnés, à l’heure d’Helsinki. Toutes les fusions non annulées sont affichées, y compris celles sans licence rendue au stock ou sans détail enregistré. Une période peut aussi être sélectionnée.',
                report_scope: 'Périmètre du rapport',
                report_latest: 'Dernier lot de fusions',
                report_dates: 'Période',
                report_from: 'Date de début',
                report_until: 'Date de fin',
                report_role: 'Rôle utilisateur',
                report_export: 'Exporter vers Excel',
                report_primary_name: 'Nom du compte conservé',
                report_primary_identifier: 'Ancien identifiant du compte conservé',
                report_source_name: 'Nom du compte fusionné',
                report_source_identifier: 'Ancien identifiant du compte fusionné',
                report_identifier_after: 'Identifiant après la fusion',
                report_released_licenses: 'Licences rendues (quantité)',
                report_released_products: 'Produits des licences rendues',
                report_show_products: 'Afficher les produits',
                report_unknown: 'Inconnu',
                report_note:
                    'Données au moment de la fusion. Données personnelles : transmettez uniquement à l’établissement concerné par un canal sécurisé.',
                report_invalid_dates: 'Saisissez des dates valides dans le bon ordre.',
                report_empty: 'Aucune fusion non annulée dans cette sélection.',
                report_error: 'Échec de l’export du rapport de fusion.',
                select_page: 'Sélectionner les fusions annulables de cette page',
                select_entry: 'Sélectionner la fusion : %{identifier}',
                cancel_selected: 'Annuler la sélection (%{count})',
                cancel_confirm: 'Annuler %{count} fusions, les plus récentes en premier. Le traitement s’arrête à la première erreur.',
                cancel_progress: 'Annulées : %{completed} / %{total}',
                cancel_result: '%{completed} / %{total} fusions annulées.',
                cancel_stopped: 'Traitement arrêté. Aucune autre fusion n’a été annulée. Erreur :',
                open: 'Historique des fusions',
                search: 'Rechercher par nom ou identifiant',
                description:
                    'Fusions effectuées pour l’établissement, de la plus récente à la plus ancienne. Vous pouvez annuler une fusion si les conditions sont remplies.',
                created: 'Date de fusion',
                primary: 'Compte principal',
                source: 'Compte fusionné',
                status: 'État',
                cancellable: 'Annulation possible',
                cancellable_until: "Jusqu’au %{date}",
                empty: 'Aucune fusion pour cet établissement.',
                load_error: 'Échec du chargement de l’historique.',
            },
            bulk: {
                report_export: '1. Exporter les doublons vers Excel',
                report_completed: '3. Rapport pour l’établissement',
                report_workflow:
                    '1. Exportez la proposition vers Excel pour validation par l’établissement.\n2. Sélectionnez les comptes approuvés, vérifiez l’aperçu et fusionnez.\n3. Exportez le rapport des fusions vers l’établissement.',
                report_workflow_note:
                    'Les fusions effectuées peuvent être consultées et annulées dans l’historique.',
                report_title: 'Liste de vérification des comptes en double',
                report_search: 'Filtre de recherche',
                report_note:
                    'Proposition à vérifier, pas de fusions effectuées. Exporte les groupes correspondant à la recherche et aux filtres sur toutes les pages, sauf les groupes exclus individuellement. Données personnelles : transmettez uniquement à l’établissement par un canal sécurisé.',
                export_included: 'Inclus dans Excel',
                export_excluded: 'Exclu d’Excel',
                export_include_group: 'Inclure le groupe %{name} dans Excel',
                export_group_count: 'Groupes à exporter : %{count} / %{total}',
                merge_group_count: 'Groupes sélectionnés pour fusion : %{count} / %{total}',
                merge_included: 'Inclus dans la fusion',
                merge_include_group: 'Inclure le groupe %{name} dans la fusion',
                export_empty: 'Aucun groupe sélectionné pour l’export Excel.',
                select_last_export: 'Sélectionner les derniers groupes exportés',
                select_last_export_help:
                    'Restaure les groupes du dernier export Excel réussi, ainsi que les choix du compte principal et de l’identifiant conservé au moment de l’export. Remplace la sélection actuelle et efface la recherche et les filtres. Les groupes modifiés ou bloqués sont ignorés. Les choix sont conservés dans ce navigateur par établissement et rôle. Disponible après le premier export. Vérifiez l’accord de l’établissement avant de fusionner.',
                last_export_restored: '%{count} groupes sélectionnés depuis le dernier export. Groupes modifiés ou bloqués ignorés : %{skipped}.',
                last_export_unavailable: 'Aucun choix d’export enregistré pour cet établissement et ce rôle.',
                last_export_invalid: 'Les choix d’export enregistrés sont illisibles. Effectuez un nouvel export Excel pour les enregistrer.',
                last_export_save_error: 'L’export Excel a réussi, mais l’enregistrement des choix a échoué.',
                export_exclusion_help:
                    'Excluez des groupes de l’export à l’aide de l’interrupteur individuel Inclus dans Excel ou des boutons de sélection de page (Sélectionner / désélectionner la page, Effacer toutes les sélections). Les groupes contenant au moins deux comptes MPASS sont toujours exclus et leur interrupteur est verrouillé. Les autres exclusions sont conservées lors des recherches, filtres, changements de page et passages entre export et fusion. Recharger la liste réinitialise les exclusions manuelles. Cela ne modifie pas les sélections de fusion.',
                report_group: 'Groupe',
                report_name: 'Nom',
                report_active_licenses: 'Licences actives de cet établissement',
                report_valid_active_licenses:
                    'Licences actives actuellement valides de cet établissement',
                report_overlapping_licenses: 'Lignes de licence actuellement valides en double de cet établissement dans le groupe',
                report_primary: 'Compte principal proposé',
                report_identifier: 'Identifiant à conserver',
                report_status: 'Remarque',
                report_review: 'Examen séparé nécessaire',
                report_proposal: 'Proposition à vérifier',
                report_processed: 'Ce groupe a déjà été traité. Actualisez la liste avant une nouvelle vérification.',
                source_account: 'Compte à fusionner',
                merge_account: 'Ouvrir l’onglet de fusion du compte %{account} (nouvel onglet)',
                search: 'Rechercher par nom, e-mail ou identifiant',
                search_help:
                    'Recherche par nom, e-mail ou identifiant. Le groupe s’affiche si l’un de ses comptes correspond.',
                filters_help:
                    'Le filtre de connexion affiche les groupes dont au moins un compte s’est connecté depuis le 1er août. Lorsque les deux filtres sont activés, un groupe doit remplir les deux conditions. Modifier un filtre efface les sélections.',
                no_search_results: 'Aucun groupe de doublons ne correspond à votre recherche.',
                logged_in_this_season: 'Connexions cette saison',
                overlapping_licenses_only: 'Groupes avec lignes de licence actuellement valides en double uniquement',
                open: 'Fusionner les doublons',
                title: 'Fusion des comptes potentiellement en double',
                subtitle: 'Établissement : %{school}',
                back: 'Retour à la liste',
                invalid_context:
                    'Sélectionnez d’abord un établissement et les doublons possibles.',
                no_permission:
                    'Vous n’avez pas le droit de fusionner des utilisateurs.',
                load_error: 'Échec du chargement des comptes en double.',
                multiple_applications:
                    'Ce groupe contient un compte lié à plusieurs services et ne peut pas être fusionné.',
                multiple_mpass:
                    'Au moins deux comptes de ce groupe ont MPASS. Il peut s’agir de personnes différentes portant le même nom complet. Le groupe est en lecture seule et ne peut pas être fusionné en masse. Examinez et traitez les comptes individuellement depuis le profil utilisateur.',
                group_count: '%{count} groupes en double trouvés',
                group_count_header: 'Groupes',
                accounts_count: '%{count} comptes',
                overlapping_licenses: 'Lignes de licence actuellement valides en double : %{count}',
                overlapping_license_count_help:
                    'Le nombre inclut les licences supplémentaires actuellement valides du même abonnement scolaire.',
                overlapping_groups: 'Groupes en double : %{count}',
                overlapping_license: 'Ligne de licence actuellement valide en double',
                overlapping_group: 'Groupe en double',
                groups: 'Groupes',
                account_details_help:
                    'Les détails du compte affichent les licences actives et les groupes (hors archivés). L’indicateur vert correspond à une licence de cet établissement (vert plein = une licence actuellement valide du même abonnement figure aussi sur un autre compte), la bordure bleue à un autre établissement et le marron à une licence personnelle. Les badges bleus indiquent les groupes communs. La date de création du compte est surlignée d’un avertissement si la durée estimée des études dépasse la durée habituelle (plus de 3 ans pour les étudiants, plus de 9 ans pour les élèves ; l’année commence le 1er août).',
                sort: 'Trier les groupes',
                sort_last_login_help: 'Affiche d’abord les groupes contenant le compte utilisé le plus récemment, selon la dernière connexion du groupe. Excel conserve cet ordre.',
                sort_licenses_help:
                    'Affiche d’abord les groupes ayant le plus de licences valides. Met aussi à jour la proposition de compte conservé (départage : connexion la plus récente).',
                sort_registered_help: 'Affiche d’abord les groupes contenant le compte le plus récent, selon la dernière inscription du groupe. Excel conserve cet ordre.',
                sort_merge_help:
                    'Triez les doublons pour examen : Connexion (plus récente d’abord), Licences (plus valides d’abord) ou Inscription (plus récents d’abord). Le tri par licences met aussi à jour la proposition de compte conservé.',
                sort_merge_reminder:
                    'Fusionnez uniquement les comptes approuvés par l’établissement. Vérifiez le compte conservé et l’identifiant avant l’aperçu et la fusion.',
                sort_export_help:
                    'L’ordre s’applique à Excel : Connexion (plus récente d’abord), Licences (plus grand nombre de licences valides d’abord) ou Inscription (plus récent d’abord). Le tri par licences met aussi à jour la proposition de compte conservé.',
                sort_last_login: 'Connexion',
                sort_licenses: 'Licences',
                sort_registered: 'Inscription',
                select_page: 'Sélectionner / désélectionner la page',
                clear_all_selection: 'Effacer toutes les sélections',
                no_groups:
                    'Aucun groupe en double trouvé pour cet établissement.',
                primary: 'Compte principal',
                identifier: 'Identifiant à conserver',
                identifier_domain: 'Privilégier un domaine de messagerie',
                identifier_domain_none: 'Aucun domaine sélectionné',
                identifier_domain_help:
                    'Choisir un domaine de messagerie propose cette adresse comme identifiant conservé pour les groupes ayant un compte correspondant.',
                account: 'Compte',
                last_login: 'Dernière connexion',
                account_created: 'Compte créé',
                study_year_warning:
                    'Selon la date de création du compte, il s’agit de la %{studyYear}e année d’études. La durée maximale habituelle est de %{maxStudyYears} années.\n\nLes études peuvent être terminées.\n\nL’année commence le 1er août. Il s’agit d’une estimation, pas d’un statut confirmé.',
                licenses: 'Licences actives (tous les titulaires)',
                license_owner_school: 'cet établissement',
                license_owner_other_school: 'autre établissement : %{school}',
                license_owner_account: 'titulaire du compte',
                license_owner_other_account: 'titulaire du compte : %{account}',
                license_owner_unknown: 'titulaire inconnu',
                license_category_school: 'Cet établissement',
                license_category_school_help:
                    'L’établissement sélectionné est le titulaire.',
                license_category_school_release_help:
                    'Les licences libérées sont reversées au pool de licences de l’établissement. Pour les élèves et les étudiants, les licences sont supprimées uniquement si elles appartiennent au même abonnement et si l’abonnement et les deux licences actives sont valides au moment de la fusion.\n\nPour les élèves, une licence valide du compte principal est conservée. Pour les étudiants, la licence activée le plus tard est supprimée.',
                license_category_other_school: 'Autre établissement',
                license_category_other_school_help:
                    'Un autre établissement est le titulaire.',
                license_category_own: 'Personnelle',
                license_category_own_help:
                    'Licence achetée par l’utilisateur.',
                license_category_unknown: 'Inconnu',
                license_category_unknown_help:
                    'Le titulaire de la licence n’a pas pu être identifié.',
                license_validity_summary:
                    '%{valid} licence(s) valide(s) sur %{active} active(s)',
                license_currently_valid: 'Licence actuellement valide',
                license_not_currently_valid: 'Licence actuellement non valide',
                show_details: 'Afficher les détails',
                hide_details: 'Masquer les détails',
                mpass: 'MPASS',
                license_names: 'Licences',
                extended_license: 'Licence prolongée (%{count} versions)',
                classes: 'Classes',
                previous: 'Précédent',
                next: 'Suivant',
                page: 'Page %{current} / %{total}',
                preview: 'Prévisualiser la sélection',
                merge_selected: 'Fusionner les groupes sélectionnés (%{count})',
                preview_title: 'Prévisualisation de la fusion',
                preview_notice:
                    'La prévisualisation ne modifie pas les comptes. Vérifiez les paires et les avertissements avant de confirmer.',
                preview_error: 'Échec de la prévisualisation',
                preview_stale:
                    'La sélection a changé. Prévisualisez-la de nouveau avant la fusion.',
                merge_error: 'Échec de la fusion',
                results_error: 'Échec du chargement des résultats',
                batch_finished: 'Traitement des groupes sélectionnés terminé.',
                progress_title: 'Fusion en cours',
                progress_count: '%{completed} / %{total} groupes traités',
                progress_elapsed: 'Temps écoulé : %{time}',
                progress_remaining: 'Temps restant estimé : environ %{time}',
                progress_estimating:
                    'L’estimation se précise pendant le traitement.',
                progress_estimate_help:
                    'L’estimation repose sur la durée moyenne des groupes traités et peut changer.',
                progress_keep_open:
                    'Gardez cette page ouverte jusqu’à la fin du traitement.',
                stop_remaining: 'Arrêter les fusions restantes',
                progress_stopping:
                    'Arrêt demandé. La requête en cours sera terminée ; aucune nouvelle fusion ne sera lancée.',
                group_stopped:
                    'La fusion des comptes restants a été arrêtée. Actualisez le groupe avant de réessayer.',
                summary_title: 'Résumé de la fusion',
                summary_stopped: 'Le traitement a été arrêté à la demande.',
                summary_fatal_error:
                    'Le traitement a été interrompu par une erreur : %{error}',
                summary_succeeded: 'Groupes traités avec succès : %{count}',
                summary_partial: 'Groupes partiellement fusionnés : %{count}',
                summary_failed: 'Groupes en échec : %{count}',
                summary_unprocessed: 'Groupes non traités : %{count}',
                summary_history_error:
                    'Les résultats de %{count} groupes n’ont pas pu être chargés. Consultez l’historique des fusions.',
                summary_unprocessed_help:
                    'Les groupes non traités restent sélectionnés jusqu’à l’actualisation de la liste. Prévisualisez-les de nouveau avant la fusion.',
                summary_retry_help:
                    'Actualisez et prévisualisez les groupes partiels ou en échec avant de réessayer. Consultez l’historique si le résultat est incertain.',
                result_success: 'Fusion réussie',
                result_partial: 'Fusion partiellement terminée',
                result_error: 'Échec de la fusion',
                result_cancelled: 'Fusion annulée',
                retry: 'Actualiser le groupe pour réessayer',
            },
            profilepage: {
                license_currently_valid: 'Licence actuellement valide',
                license_not_currently_valid:
                    'La licence active n’est pas actuellement valide',
                license_overlapping:
                    'Une licence actuellement valide de l’établissement, issue du même abonnement, figure sur un autre compte.',
                license_overlapping_student:
                    'La licence activée le plus tard est supprimée.',
                license_overlapping_pupil:
                    'La licence du compte principal est conservée.',
                cancel_button: 'Annuler la fusion',
                cancel_confirm_title: 'Annuler cette fusion ?',
                cancel_confirm_message:
                    'Les données du compte fusionné seront restaurées dans un compte séparé. Cette action est irréversible.',
                cancel_deadline_passed:
                    'Le délai d’annulation est dépassé.',
                cancel_materials_merged:
                    'Les contenus ont déjà été fusionnés ; l’annulation n’est plus possible.',
                cancel_success: 'Fusion annulée',
                cancel_error: 'Échec de l’annulation de la fusion',
            },
            cancel: 'Annuler',
            conflict: {
                firstname: 'Le prénom ne correspond pas',
                lastname: 'Le nom ne correspond pas',
                mpass: 'Les deux comptes ont une liaison MPASS',
                last_login: 'La dernière connexion du compte source est plus récente',
                restricted_material:
                    'Le compte source possède des licences de matériel restreint.',
            },
        },
        pos: {
            search: 'Rechercher',
            configuration: 'Configuration',
            language: 'Langue',
            theme: {
                name: 'Theme',
                light: 'Clair',
                dark: 'Obscur',
            },
            filter: 'Filtré par',
            dashboard: {
                monthly_revenue: 'CA à 30 jours',
                month_history: "Chiffre d'affaire sur 30 jours",
                new_orders: 'Nouvelles commandes',
                pending_reviews: 'Commentaires à modérer',
                new_customers: 'Nouveaux clients',
                all_customers: 'Tous les clients',
                all_reviews: 'Tous les avis',
                pending_orders: 'Commandes à traiter',
                order: {
                    items: 'par %{customer_name}, un poster |||| par %{customer_name}, %{nb_items} posters',
                },
                timeline: 'Timeline',
                welcome: {
                    title: 'Bienvenue sur la démo de react-admin enterprise edition',
                    subtitle:
                        "Ceci est le back-office d'un magasin de posters imaginaire mettant en oeuvre les modules de l'édition entreprise. N'hésitez pas à explorer et à modifier les données. La démo s'exécute en local dans votre navigateur, et se remet à zéro chaque fois que vous rechargez la page.",
                    ra_button: 'Site web de react-admin entreprise',
                    demo_button: 'Voir tous les modules',
                    github_button: 'Voir le code source',
                },
            },
            menu: {
                sales: 'Ventes',
                catalog: 'Catalogue',
                my_queries: 'Mes recherches',
                customers: 'Clients',
                new_customers: 'Nouveaux clients',
                all_customers: 'Tous les clients',
                visitors: 'Visiteurs',
                all_reviews: 'Tous les avis',
                pending_reviews: 'Avis en attende de validation',
                bad_reviews: 'Mauvais avis',
            },
            reviews: {
                accepted: 'Acceptés',
                rejected: 'Rejetés',
                pending: 'En attente',
            },
        },
        resources: {
            campaign: {
                name: 'Campagne |||| Campagnes',
                filters: {
                    search: 'Rechercher par nom de campagne',
                    all: 'Toutes',
                    ongoing: 'En cours',
                    upcoming: 'À venir',
                    ended: 'Terminée',
                    inactive: 'Inactive',
                    period: 'Valide pendant la période sélectionnée',
                    from: 'Date de début',
                    until: 'Date de fin',
                    invalid_dates: 'La date de fin ne doit pas précéder la date de début.',
                    period_help: 'Affiche les campagnes dont la période de validité recoupe la période sélectionnée. Dates à l’heure d’Helsinki.',
                },
                fields: {
                    name: 'Nom de la campagne',
                    start_date: 'Début',
                    end_date: 'Fin',
                    books: 'Livres',
                    isbn: 'ISBN',
                    title: 'Titre du livre',
                    targets: 'Public cible',
                    target_type: 'Type d’identifiant cible',
                    target_list: 'Liste des cibles',
                    created: 'Création',
                    csv_email: 'Destinataire du CSV',
                    csv_weekdays: 'Jours d’export (ma,ti,ke,to,pe,la,su)',
                    csv_time: 'Heure d’export (heure d’Helsinki, HH:mm)',
                    settings_json: 'Autres paramètres de campagne (JSON)',
                    export_schedule: 'Export CSV',
                    target_count: 'Cibles',
                    responded_count: 'Réponses',
                    response_rate: 'Taux de réponse',
                    accepted_count: 'Acceptées',
                    rejected_count: 'Refusées',
                    user_guid: 'GUID utilisateur',
                    user_name: 'Enseignant',
                    school_guid: 'GUID de l’établissement',
                    school_name: 'Établissement',
                    selected_books: 'ISBN sélectionnés',
                    responded_at: 'Date de réponse',
                    status: 'Statut',
                },
                actions: {
                    template: 'Modèle Excel',
                    export_workbook: 'Télécharger la campagne comme modèle Excel',
                    import: 'Importer une campagne',
                    export: 'Télécharger les commandes acceptées en CSV',
                    exporting: 'Téléchargement…',
                    export_responses: 'Télécharger toutes les réponses en CSV',
                    exporting_responses: 'Téléchargement des réponses…',
                    email: 'Envoyer les commandes acceptées par e-mail',
                    emailing: 'Envoi…',
                },
                status: {
                    active: 'Active',
                    inactive: 'Inactive',
                    pending: 'En attente',
                    accepted: 'Acceptée',
                    rejected: 'Refusée',
                },
                target_types: {
                    user_guid: 'GUID utilisateur Nova',
                    crm_guid: 'GUID CRM',
                },
                helpers: {
                    target: 'Saisissez un GUID par ligne. Sélectionnez ci-dessus si les identifiants sont des GUID utilisateur Nova ou des GUID CRM.',
                    status: 'Les campagnes actives sont visibles par les enseignants pendant leur période de validité. Les campagnes inactives sont masquées, ne peuvent pas recevoir de réponses et ne déclenchent pas d’exports CSV programmés. Les exports manuels restent disponibles.',
                    csv_email: 'Adresse e-mail à laquelle envoyer les commandes acceptées de la campagne au format CSV. Laissez vide pour utiliser le destinataire par défaut défini dans les paramètres d’administration.',
                    weekdays: 'Utilisez les abréviations finlandaises ma,ti,ke,to,pe,la,su (lundi–dimanche), séparées par des virgules. Vide = export manuel uniquement.',
                    time: 'Heure locale au format 24 heures (Europe/Helsinki).',
                    settings: 'Autres paramètres de campagne au format objet JSON.',
                },
                tabs: {
                    details: 'Détails de la campagne',
                    responses: 'Public cible et réponses',
                    daily_report: 'Rapport quotidien',
                    export_history: 'Historique des envois',
                },
                daily_report: {
                    date: 'Date',
                    responses: 'Réponses par jour',
                    books: 'Livres sélectionnés par jour',
                    selected_books: 'Total des livres',
                    total: 'Total',
                    empty: 'Cette campagne ne contient pas encore de réponses.',
                    description: 'Les dates des réponses suivent le fuseau Europe/Helsinki. Le nombre de livres indique combien de réponses acceptées ont sélectionné chaque ISBN. Seuls les jours avec des réponses sont affichés.',
                },
                delivery_report: {
                    time: 'Date et heure',
                    type: 'Type d’envoi',
                    status: 'Statut',
                    attempts: 'Tentatives',
                    error_detail: 'Erreur',
                    empty: 'Aucun historique d’envoi CSV pour cette campagne.',
                    error: 'Impossible de charger l’historique des envois.',
                    description: 'Liste les tâches cron planifiées et les e-mails CSV manuels. Envoyé signifie que Mailgun a accepté le message ; la livraison finale au destinataire n’est pas confirmée.',
                    types: {
                        scheduled: 'Cron planifié',
                        manual: 'Manuel',
                    },
                    statuses: {
                        sending: 'En cours',
                        sent: 'Accepté par Mailgun',
                        failed: 'Échec',
                    },
                },
                import_success: 'Campagne importée depuis Excel.',
                template_error: 'Impossible de télécharger le modèle Excel.',
                workbook_error: 'Échec de l’export Excel de la campagne.',
                export_error: 'Échec de l’export CSV.',
                email_success: 'Réponses envoyées au destinataire CSV de la campagne.',
                email_error: 'Échec de l’envoi des réponses par e-mail.',
                recipient_missing: 'Aucun destinataire CSV configuré.',
                responses_error: 'Impossible de charger les réponses.',
                manual_export: 'Manuel',
            },
            teacher: {
                actions: {
                    export_duplicates: 'Exporter les doublons vers Excel',
                },
                fields: {
                    username_column: 'Nom d’utilisateur',
                    validfrom: 'Valide à partir du',
                    validto: 'Valide jusqu\'au',
                    public: {
                        validfrom: 'Valide à partir du',
                        validto: 'Valide jusqu\'au',
                    },
                },
            },
            customers: {
                name: 'Client |||| Clients',
                fields: {
                    address: 'Rue',
                    birthday: 'Anniversaire',
                    city: 'Ville',
                    commands: 'Commandes',
                    first_name: 'Prénom',
                    first_seen: 'Première visite',
                    groups: 'Segments',
                    has_newsletter: 'Abonné à la newsletter',
                    has_ordered: 'A commandé',
                    last_name: 'Nom',
                    last_seen: 'Vu le',
                    last_seen_gte: 'Vu depuis',
                    latest_purchase: 'Dernier achat',
                    name: 'Nom',
                    total_spent: 'Dépenses',
                    zipcode: 'Code postal',
                    password: 'Mot de passe',
                    confirm_password: 'Confirmez le mot de passe',
                    nb_commands: 'Commandes',
                },
                filters: {
                    last_visited: 'Dernière visite',
                    today: "Aujourd'hui",
                    this_week: 'Cette semaine',
                    last_week: 'La semaine dernière',
                    this_month: 'Ce mois-ci',
                    last_month: 'Le mois dernier',
                    earlier: 'Plus tôt',
                    has_ordered: 'A déjà commandé',
                    has_newsletter: 'Abonné newsletter',
                    group: 'Segment',
                },
                fieldGroups: {
                    identity: 'Identité',
                    address: 'Adresse',
                    stats: 'Statistiques',
                    history: 'Historique',
                    password: 'Mot de passe',
                    change_password: 'Changer le mot de passe',
                },
                page: {
                    delete: 'Supprimer le client',
                },
                errors: {
                    password_mismatch:
                        'La confirmation du mot de passe est différent du mot de passe.',
                },
            },
            commands: {
                name: 'Commande |||| Commandes',
                amount: '1 commande |||| %{smart_count} commandes',
                title: 'Commande n°%{reference}',
                fields: {
                    basket: {
                        delivery: 'Frais de livraison',
                        reference: 'Référence',
                        quantity: 'Quantité',
                        sum: 'Sous-total',
                        tax_rate: 'TVA',
                        total: 'Total',
                        unit_price: 'P.U.',
                    },
                    address: 'Adresse',
                    customer_id: 'Client',
                    date_gte: 'Emises depuis',
                    date_lte: 'Emises avant',
                    nb_items: 'Nb articles',
                    reference: 'Référence',
                    returned: 'Annulée',
                    status: 'Etat',
                    total_gte: 'Montant minimum',
                },
            },
            invoices: {
                name: 'Facture |||| Factures',
                fields: {
                    id: 'Numéro',
                    date: 'Date de facture',
                    customer_id: 'Client',
                    command_id: 'Commande',
                    date_gte: 'Emises depuis',
                    date_lte: 'Emises avant',
                    address: 'Adresse',
                    total_ex_taxes: 'Montant HT',
                    delivery_fees: 'Frais de livraison',
                    taxes: 'TVA',
                },
            },
            products: {
                name: 'Poster |||| Posters',
                fields: {
                    id: 'Identifiant',
                    category_id: 'Catégorie',
                    height_gte: 'Hauteur mini',
                    height_lte: 'Hauteur maxi',
                    height: 'Hauteur',
                    image: 'Photo',
                    price: 'Prix',
                    reference: 'Référence',
                    sales: 'Ventes',
                    stock_lte: 'Stock faible',
                    stock: 'Stock',
                    thumbnail: 'Aperçu',
                    width_gte: 'Largeur mini',
                    width_lte: 'Margeur maxi',
                    width: 'Largeur',
                },
                tabs: {
                    image: 'Image',
                    details: 'Détails',
                    description: 'Description',
                    reviews: 'Commentaires',
                },
                filters: {
                    categories: 'Catégories',
                    stock: 'Stock',
                    no_stock: 'En rupture',
                    low_stock: '1 - 9 unités',
                    average_stock: '10 - 49 unités',
                    enough_stock: '50 unités et plus',
                    sales: 'Ventes',
                    best_sellers: 'Meilleures ventes',
                    average_sellers: 'Moyennes',
                    low_sellers: 'Peu vendu',
                    never_sold: 'Jamais vendu',
                },
            },
            categories: {
                name: 'Catégorie |||| Catégories',
                fields: {
                    name: 'Nom',
                    products: 'Produits',
                },
                actions: {
                    create_product: 'Créer un Poster',
                },
            },
            reviews: {
                name: 'Commentaire |||| Commentaires',
                amount: '1 commentaire |||| %{smart_count} commentaires',
                relative_to_poster: 'Commentaire sur',
                detail: 'Détail du commentaire',
                fields: {
                    customer_id: 'Client',
                    command_id: 'Commande',
                    product_id: 'Produit',
                    date_gte: 'Publié depuis',
                    date_lte: 'Publié avant',
                    date: 'Date',
                    comment: 'Texte',
                    status: 'Statut',
                    rating: 'Classement',
                },
                action: {
                    accept: 'Accepter',
                    reject: 'Rejeter',
                },
                notification: {
                    approved_success: 'Commentaire approuvé',
                    approved_error: 'Erreur: Commentaire non approuvé',
                    rejected_success: 'Commentaire rejeté',
                    rejected_error: 'Erreur: Commentaire non rejeté',
                },
            },
            segments: {
                name: 'Segments',
                fields: {
                    customers: 'Clients',
                    name: 'Nom',
                },
                data: {
                    compulsive: 'Compulsif',
                    collector: 'Collectionneur',
                    ordered_once: 'A commandé',
                    regular: 'Régulier',
                    returns: 'A renvoyé',
                    reviewer: 'Commentateur',
                },
            },
            stores: {
                name: 'Magasins',
                fields: {
                    city: 'Ville',
                    country: 'Pays',
                    address: 'Adresse',
                    created_at: 'Ouvert le',
                },
            },
            tours: {
                name: 'Visites',
            },
            locks: {
                overlay: "En cours d'édition par %{name}",
            },
            events: {
                name: 'Événements',
            },
            user: {
                actions: {
                    mpass_profile: 'Informations de base MPASS',
                    debug_mpass_masked: 'Test masqué (localhost)',
                    debug_mpass_masked_help: 'Les noms, identifiants personnels et noms de classe sont fictifs. Les établissements, organisations et rôles restent réels : l’anonymat n’est pas garanti. Vérifiez l’environnement de test : la connexion peut créer des comptes et utiliser les intégrations externes configurées.',
                    mpass_name: 'Nom fourni par MPASS',
                    mpass_nickname: 'Prénom usuel fourni par MPASS (nickname)',
                    mpass_name_changed: 'MPASS contient une indication de changement de nom. Ancien nom et date : %{previous}',
                    mpass_schools: 'Établissements',
                    mpass_roles: 'Rôles',
                    mpass_classes: 'Classes / groupes',
                    mpass_last_login: 'Dernière connexion MPASS enregistrée',
                },
                session: {
                    remaining: 'Session restante',
                    active_sessions: 'sessions actives',
                    longest: 'la plus longue',
                    confirm_remove:
                        "Êtes-vous sûr de vouloir supprimer la session et déconnecter l'utilisateur?",
                    confirm_remove_all:
                        "Êtes-vous sûr de vouloir supprimer toutes les %{count} sessions et déconnecter l'utilisateur de tous les appareils?",
                },
            },
        },
        tours: {
            action: {
                play: 'Jouer',
            },
            message: {
                played_on: 'Visité le %{date}',
                never_played: 'Non visité',
            },
            'ra-preferences': {
                comment:
                    "Enregistre les preferences de l'utilisateur (langue, ui, filtres, colonnes affichées, etc) dans l'espace de stockage local du navigateur",
                intro: 'ra-preferences apporte de nombreux modules pré-configués, comme ce sélecteur de thème. Essayez-le: il fonctionne!',
                language_switcher: 'Ou ce sélecteur de langue...',
                persisted_queries:
                    "Il propose également des requêtes persistantes. Par exemple, enregistrons les filtres pour les visiteurs d'aujourd'hui qui ont effectivement commandé quelque chose.",
                persisted_queries_result:
                    'Elle est désormais enregistrée localement dans le navigateur !',
                list_customization:
                    'Il propose même des composants plus avancés, comme cet outil de personnalisation des listes.',
                list_customization_columns:
                    "Vous pouvez y sélectionner le mode d'affichage de la liste ou les informations que vous souhaitez voir",
                hook: "Il expose des hooks afin que vous puissiez enregistrer ce que vous voulez également. Par exemple, l'état de cette étape particulière. Essayez de recharger la page!",
            },
            'ra-search': {
                comment:
                    "Connectez votre moteur de recherche et permettez aux utilisateurs d'effectuer des recherches dans toutes les ressources grâce à une boîte Omnisearch intelligente.",
                intro: "Ceci est la boîte Omnisearch intelligente. Elle permet aux utilisateurs d'effectuer des recherches dans toutes les ressources.",
                customize:
                    "Vous pouvez personnaliser les résultats de la recherche à volonté et rediriger vers n'importe quelle ressource. Par exemple, cliquons sur le premier client.",
                preserved_across_navigation:
                    'La requête de recherche et les résultats sont conservés après la navigation.',
            },
            'ra-navigation-breadcrumb': {
                comment:
                    'Gardez une trace de votre localisation dans le site et naviguez facilement.',
                intro: 'Le breacrumb indique que nous sommes sur la page des affiches.',
                edit: "Éditons l'une de ces affiches.",
                sync: "Le breacrumb a été modifié pour nous suivre jusqu'à cette page d'édition.",
                navigate:
                    'Essayons de naviguer ailleurs en utilisant une entrée du Menu.',
                sync2: 'Le breadcrumb continue de montrer notre position exacte.',
                clickable:
                    "Les utilisateurs peuvent cliquer directement sur les éléments du breadcrumb pour naviguer. Allons à la page d'accueil.",
                dashboard:
                    "Par défaut, il n'y a pas de breadcrumb affiché sur la page d'accueil. Maintenant, c'est à vous d'utiliser le composant breadcrumb pour construire votre propre breadcrumb !",
            },
            'ra-realtime': {
                comment:
                    "Activez le temps réel sur les vues de menu, de liste, d'affichage et d'édition - ou partout où vous le souhaitez.",
                intro: "On dirait que vous venez d'avoir de nouvelles commandes, vérifions...",
                new_orders:
                    'Vos nouvelles commandes peuvent se démarquer des autres',
                newest: 'Et les commandes les plus récentes apparaissent même lorsque vous êtes sur la page.',
                locks: 'Vous pouvez verrouiller les ressources en temps réel (celle-ci sera déverrouillée dans quelques secondes).',
                end: "Voilà pour ra-realtime. Jetez un oeil aux autres visites pour découvrir d'autres fonctionnalités de la version entreprise !",
            },
            'ra-editable-datagrid': {
                comment:
                    'Modifiez rapidement vos données sans quitter votre Datagrid.',

                intro: "En passant la souris sur une ligne, une barre d'outils s'affiche, permettant de modifier ou de supprimer l'enregistrement. Voyons ce qui se passe lorsque l'on modifie une ligne en cliquant sur le bouton Modifier (ou en cliquant directement dans la ligne).",
                edit: "Vous pouvez modifier un enregistrement sans quitter la grille de données ! Changeons l'adresse.",
                save: "Après l'édition, cliquez sur le bouton Enregistrer dans la ligne.",
                create: 'Le Datagrid modifiable prend également en charge la création en ligne.',
                forms: "Un formulaire d'édition/création de ligne peut contenir des entrées de tout type (texte, date, nombre, etc.).",
                create_save:
                    'Cliquez sur le bouton Enregistrer pour soumettre le formulaire et créer un nouvel enregistrement.',
            },
            'ra-tree': {
                comment:
                    "Modifiez et visualisez des structures arborescentes. Réorganisez par glisser-déposer. S'adapte à toute structure de données sur le backend (parent_id, enfants, ensembles imbriqués, etc.)...",
                intro: 'ra-tree permet de manipuler les arbres de données avec facilité, quelle que soit la structure de données que vous utilisez en arrière-plan.',
                infinite_levels:
                    "Il prend en charge l'expansion ou la réduction des nœuds pour un nombre infini de niveaux.",
                changes:
                    'Vous pouvez même ajouter une nouvelle catégorie, ou les réorganiser, essayez-le !',
            },
            'ra-markdown': {
                comment:
                    "Lire des données Markdown et les modifier à l'aide d'un éditeur WYSIWYG dans votre administration.",
                intro: "Ceci est un poster, l'un des produits vendus par notre boutique, allons voir ses détails.",
                editor_location:
                    "L'éditeur markdown se trouve dans l'onglet description.",
                editor: 'Ici. Essayez de jouer avec son markdown, mettez-le en gras, ajoutez des titres !',
                wysiwyg: 'Par défaut, vous êtes en mode WYSIWYG.',
                raw: "Mais vous pouvez passer à l'édition brute avec ce bouton.",
                show: "Les données markdown transformées peuvent ensuite être affichées dans un aperçu ou à l'endroit de votre choix.",
            },
        },
    }
);

export default customFrenchMessages;
